<script lang="ts" setup>
const container = ref<HTMLDivElement | null>(null)
const isHovered = ref(false)
const contentWidth = ref(0)
const containerWidth = ref(0)

const DEFAULT_SPEED = 60

const duration = computed(() => {
  if (!contentWidth.value) return '20s'
  const seconds = Math.max(1, contentWidth.value / DEFAULT_SPEED)
  return `${seconds}s`
})

const trackStyle = computed(() => ({
  '--duration': duration.value,
  '--state': isHovered.value ? 'paused' : 'running',
  '--distance': `${contentWidth.value}px`,
}))

const repeats = computed(() => {
  if (!contentWidth.value || !containerWidth.value) return 2
  
  const minTrackWidth = containerWidth.value + contentWidth.value
  const requiredCopies = Math.ceil(minTrackWidth / contentWidth.value)

  return Math.max(2, requiredCopies)
})

const measure = () => {
  const firstSegment = container.value?.querySelector('.marquee__content') as HTMLDivElement
  contentWidth.value = firstSegment ? firstSegment.offsetWidth : 0
  containerWidth.value = container.value ? container.value.offsetWidth : 0
}

onMounted(() => {
  const ro = new ResizeObserver(() => measure())
  
  if (container.value) ro.observe(container.value)
  const firstSegment = container.value?.querySelector('.marquee__content') as HTMLDivElement
  if (firstSegment) ro.observe(firstSegment)

  measure()
  
  onBeforeUnmount(() => {
    ro.disconnect()
  })
})

</script>

<template>
  <div
    ref="container"
    class="marquee"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div
      class="marquee__track"
      :style="trackStyle"
    >
      <div
        v-for="(n, idx) in repeats"
        :key="`segment-${n}`"
        class="marquee__content"
        :aria-hidden="idx > 0"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.marquee {
  width: 100%;
  overflow: hidden;

  &__track {
    display: flex;
    width: max-content;
    animation: marquee-scroll var(--duration) linear infinite;
    animation-play-state: var(--state);
  }

  &__content {
    display: inline-flex;
    align-items: center;
  }
}

@keyframes marquee-scroll {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(calc(var(--distance) * -1));
  }
}
</style>

